import { ArpMedia, PaginatedResponse } from '@arpeggi/shared'
import axios from 'axios'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useQuery } from 'react-query'
import { useAuth } from '../../__legacy__/hooks/useAuth'
import { useArpMedia } from '../../__legacy__/queries/arpMedia'
import { useSamplePack } from '../../__legacy__/queries/samplePacks'
import { ApiService } from '../api'
import ArpRegistry from '../arp-registry/ArpRegistry'
import { useAsyncMemo } from '../react-utils'
import { CurrentPageMediaContext } from './CurrentPageContext'

/**
 * Helper funcctions for the useVerifyMedia hook
 */
const useVerifyMediaHelpers = (currentPageContext: CurrentPageMediaContext) => {
  const {
    verifyMediaForSamplePack,
    unverifyMediaForSamplePack,
    verifyMediaForArpID,
    unverifyMediaForArpID
  } = ApiService.VerifiedArpMedia.hooks()
  const { data: sp } = useSamplePack({
    id: currentPageContext.samplePackId,
    options: { enabled: currentPageContext.samplePackId !== undefined }
  })
  const { data: media } = useArpMedia({
    id: currentPageContext.arpMediaId,
    options: { enabled: currentPageContext.arpMediaId !== undefined }
  })
  const flags = useFlags()
  const { user } = useAuth()

  // gets the verified IDs for the given arp media
  const getVerifiedMediaIds = async (): Promise<number[]> => {
    if (!currentPageContext.arpMediaId) return []
    const verified = await axios.get<PaginatedResponse<ArpMedia>>(
      `/api/v2/arp-media/${currentPageContext.arpMediaId}/verified-arp-medias`
    )
    const remixes = verified.data.data
    ArpRegistry.setMedias(remixes)
    return remixes.map((arp) => arp.id)
  }

  // gets the verified IDs for a given sample pack
  const getVerifiedSamplePackIds = async (): Promise<number[]> => {
    if (!currentPageContext.samplePackId) return []
    const verified = await axios.get<PaginatedResponse<ArpMedia>>(
      `/api/v2/sample-pack/${currentPageContext.samplePackId}/verified-arp-medias`
    )
    const remixes = verified.data.data
    ArpRegistry.setMedias(remixes)
    return remixes.map((arp) => arp.id)
  }

  // verifies or unverifies the given arp media for an arp media element or a sample pack
  const onVerifyMedia = async (arpMediaID: number, verifiedIDs: number[]): Promise<void> => {
    if (currentPageContext.arpMediaId) {
      if (verifiedIDs.includes(arpMediaID)) {
        await unverifyMediaForArpID(currentPageContext.arpMediaId, arpMediaID)
      } else {
        await verifyMediaForArpID(currentPageContext.arpMediaId, arpMediaID)
      }
    } else if (currentPageContext.samplePackId) {
      if (verifiedIDs.includes(arpMediaID)) {
        await unverifyMediaForSamplePack(currentPageContext.samplePackId, arpMediaID)
      } else {
        await verifyMediaForSamplePack(currentPageContext.samplePackId, arpMediaID)
      }
    }

    return
  }

  // gets whether the given currentPageContext and it's media
  // will allow the user to verify new media
  const getIsVerifiableArpMedia = async (): Promise<boolean> => {
    if (!currentPageContext.arpMediaId) return false
    const flagContainsMedia = flags?.verifiableArpMedias?.includes(currentPageContext.arpMediaId)
    const userCanVerify =
      media?.user.walletAddress === user?.walletAddress || user?.type === 'admin'

    return flagContainsMedia && userCanVerify
  }

  // gets whether the given currentPageContext and sample pack
  // will allow the user to verify new media
  const getIsVerifiableSamplePack = async (): Promise<boolean> => {
    if (!currentPageContext.samplePackId) return false

    const userCanVerify = sp?.user.walletAddress === user?.walletAddress || user?.type === 'admin'

    return userCanVerify
  }

  return {
    onVerifyMedia,
    getVerifiedSamplePackIds,
    getVerifiedMediaIds,
    getIsVerifiableArpMedia,
    getIsVerifiableSamplePack
  }
}

const useVerifiedIDs = (currentPageContext: CurrentPageMediaContext) => {
  const { getVerifiedMediaIds, getVerifiedSamplePackIds } =
    useVerifyMediaHelpers(currentPageContext)
  const type = currentPageContext.arpMediaId !== undefined ? 'media' : 'sp'
  const id = currentPageContext.arpMediaId || currentPageContext.samplePackId
  return useQuery(['verified-ids', id, type], async () => {
    if (type === 'media') return await getVerifiedMediaIds()
    else if (type === 'sp') return await getVerifiedSamplePackIds()
  })
}

/**
 * Given the current page's context, will return which arp IDs are verified,
 * whether media is verifiable in tables right now, and a verify/unverify function
 * @param currentPageContext A dictionary containing context on what the current page is (e.g.
 * if arpMediaId is 100, then any verify action taken/the verified IDs will be for arp with ID 100)
 */
export const useVerifyMedia = (currentPageContext: CurrentPageMediaContext) => {
  const { onVerifyMedia, getIsVerifiableArpMedia, getIsVerifiableSamplePack } =
    useVerifyMediaHelpers(currentPageContext)
  const { data: verifiedIDs } = useVerifiedIDs(currentPageContext)

  const { value: isVerifiable } = useAsyncMemo(async () => {
    if (currentPageContext.arpMediaId !== undefined) {
      return await getIsVerifiableArpMedia()
    } else if (currentPageContext.samplePackId !== undefined) {
      return await getIsVerifiableSamplePack()
    }
    return false
  }, [currentPageContext])

  const onVerify = async (arpMediaID: number): Promise<void> => {
    await onVerifyMedia(arpMediaID, verifiedIDs)
  }

  return { isVerifiable, verifiedIDs: verifiedIDs || [], onVerify }
}
